import { Link, useLocation } from "wouter";
import { Menu, X } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
const shrinkMDLogo = "/img/shrinkmd-logo.webp";
const shrinQLogo = "/img/shrinq-logo-on-dark.png";
import { EducationalDisclaimer } from "@/components/EducationalDisclaimer";
import { NetworkFooter } from "@/components/NetworkFooter";
import { ConsentBanner } from "@/components/ConsentBanner";
import { withNetworkUtm } from "@/lib/network";

export function SiteLayout({ children }: { children: React.ReactNode }) {
  const [rawLocation] = useLocation();
  // Cloudflare Pages serves every page at /route/ (trailing slash) while the
  // prerender and in-app links use /route. Normalize so the active nav state
  // is the same on the server snapshot and in the browser (otherwise the
  // hydration check fails and React re-renders the whole page).
  const location =
    rawLocation.length > 1 ? rawLocation.replace(/\/+$/, "") : rawLocation;
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const firstMobileLinkRef = useRef<HTMLAnchorElement>(null);

  const navItems = [
    { href: "/", label: "Home" },
    { href: "/about", label: "About" },
    { href: "/perspectives", label: "Perspectives" },
    { href: "/books", label: "Books" },
    { href: "/projects", label: "Projects" },
    { href: "/media", label: "Media" },
    { href: "/speaking", label: "Speaking" },
    { href: "/founder-of-the-shrink-network", label: "The Shrink Network" },
    { href: "/contact", label: "Contact" },
  ];

  const legalItems = [
    { href: "/terms", label: "Terms" },
    { href: "/privacy", label: "Privacy" },
    { href: "/cookies", label: "Cookies" },
    { href: "/copyright", label: "Copyright" },
    { href: "/disclaimer", label: "Disclaimer" },
    { href: "/accessibility", label: "Accessibility" },
    { href: "/community-guidelines", label: "Community Guidelines" },
    { href: "/security", label: "Security" },
  ];

  const siteResourceItems = [
    { href: "/faq", label: "FAQ" },
    { href: "/press", label: "Press" },
    { href: "/notifications", label: "Notifications" },
    { href: "/editorial-process", label: "Editorial Process" },
    { href: "/disclosures", label: "Disclosures" },
    { href: "/corrections", label: "Corrections" },
    { href: "/evidence", label: "Evidence Methodology" },
    { href: "/medical-review-board", label: "Medical Review Board" },
    { href: "/ai-policy", label: "AI Use Policy" },
    { href: "/verified", label: "Verify Dr. Refai" },
  ];

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Move focus into mobile menu when opened, return on close
  useEffect(() => {
    if (isMobileMenuOpen) {
      firstMobileLinkRef.current?.focus();
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          setIsMobileMenuOpen(false);
          menuButtonRef.current?.focus();
        }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
    }
    return undefined;
  }, [isMobileMenuOpen]);

  return (
    <div className="min-h-screen flex flex-col bg-background font-sans selection:bg-accent selection:text-white">
      {/* Skip to content (visible on focus) */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background"
      >
        Skip to main content
      </a>

      <header
        role="banner"
        className="sticky top-0 z-50 w-full border-b border-border/40 bg-background/80 backdrop-blur-xl transition-all duration-300"
      >
        <div className="container mx-auto px-4 md:px-8 h-20 flex items-center justify-between">
          <div className="flex flex-col justify-center leading-none">
            <Link
              href="/"
              className="text-lg md:text-xl font-serif font-medium tracking-tight hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm flex items-center gap-2 w-fit"
              aria-label="Shariq Refai, MD, MBA, home"
            >
              <span className="font-bold">Shariq Refai,</span>
              <span className="text-muted-foreground font-light">MD, MBA</span>
            </Link>
            <a
              href={withNetworkUtm("https://shrinknetwork.com/", "site_layout")}
              rel="noopener"
              className="mt-1.5 w-fit text-[0.6rem] md:text-xs font-bold uppercase tracking-[0.2em] text-accent hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm"
            >
              Part of The Shrink Network
            </a>
            <span className="mt-1 font-serif italic text-primary text-[0.5rem] md:text-[0.64rem] leading-none no-underline [text-decoration:none]">
              Perspective. Understood.
            </span>
          </div>

          <div className="flex items-center gap-6">
            <nav
              aria-label="Primary"
              className="hidden lg:flex items-center gap-6"
            >
              {navItems.map((item) => {
                const isCurrent = location === item.href;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    aria-current={isCurrent ? "page" : undefined}
                    className={`relative text-sm font-medium tracking-wide transition-colors hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm px-2 py-1 ${
                      isCurrent
                        ? "text-primary"
                        : "text-muted-foreground"
                    }`}
                  >
                    {item.label}
                    {isCurrent && (
                      <motion.div 
                        layoutId="nav-indicator"
                        className="absolute -bottom-[26px] left-0 right-0 h-[2px] bg-accent"
                        initial={false}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            <div className="flex items-center gap-2">
              <button
                ref={menuButtonRef}
                type="button"
                className="lg:hidden p-3 -mr-3 text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background rounded-sm min-w-[44px] min-h-[44px] inline-flex items-center justify-center"
                onClick={() => setIsMobileMenuOpen((o) => !o)}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                aria-controls="mobile-nav"
              >
                {isMobileMenuOpen ? (
                  <X size={24} aria-hidden="true" />
                ) : (
                  <Menu size={24} aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            id="mobile-nav"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 top-20 z-40 bg-background/95 backdrop-blur-xl lg:hidden border-b border-border flex flex-col shadow-lg"
          >
            <nav aria-label="Mobile" className="flex flex-col p-6 gap-2">
              {navItems.map((item, i) => {
                const isCurrent = location === item.href;
                return (
                  <motion.div
                    key={item.href}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      href={item.href}
                      ref={i === 0 ? firstMobileLinkRef : undefined}
                      aria-current={isCurrent ? "page" : undefined}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block text-lg font-serif py-3 px-4 transition-colors border-l-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-r-md ${
                        isCurrent
                          ? "border-accent bg-accent/5 text-primary"
                          : "border-transparent text-muted-foreground hover:bg-muted/50 hover:text-primary"
                      }`}
                    >
                      {item.label}
                    </Link>
                  </motion.div>
                );
              })}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <main
        id="main"
        role="main"
        tabIndex={-1}
        className="flex-1 flex flex-col focus:outline-none"
        aria-hidden={isMobileMenuOpen ? true : undefined}
        {...(isMobileMenuOpen ? { inert: "" as unknown as undefined } : {})}
      >
        {children}
      </main>

      <footer
        role="contentinfo"
        className="border-t border-border mt-auto bg-[#171717] text-white"
        aria-hidden={isMobileMenuOpen ? true : undefined}
        {...(isMobileMenuOpen ? { inert: "" as unknown as undefined } : {})}
      >
        {/* Crisis banner */}
        <div
          role="note"
          aria-label="Crisis resources"
          className="bg-[#0F0F0F] border-b border-white/10"
        >
          <div className="container mx-auto px-4 md:px-8 py-4 text-sm text-gray-200 font-light leading-relaxed">
            <strong className="font-medium text-white">In crisis?</strong>{" "}
            If you are in crisis or feel unsafe, call or text{" "}
            <a
              href="tel:988"
              className="underline decoration-accent decoration-2 underline-offset-4 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
            >
              988
            </a>{" "}
            in the United States, call{" "}
            <a
              href="tel:911"
              className="underline decoration-accent decoration-2 underline-offset-4 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
            >
              911
            </a>
            , or go to the nearest emergency room.
          </div>
        </div>

        <div className="container mx-auto px-4 md:px-8 py-16 grid gap-12 md:grid-cols-2 lg:grid-cols-[1fr_auto_auto_auto] lg:gap-16">
          <div className="max-w-2xl">
            <Link
              href="/"
              className="text-xl font-serif font-medium mb-6 block focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
            >
              Shariq Refai, MD, MBA
            </Link>
            <EducationalDisclaimer variant="footer" className="mt-4" />
            <p className="text-xs text-gray-400 leading-relaxed font-light mt-3">
              Clinical care inquiries should be directed to shrinkmd.com.
            </p>

            <div className="mt-8 pt-6 border-t border-white/10 flex items-center gap-4">
              <span className="text-xs uppercase tracking-widest text-gray-400 font-medium">
                Founder of
              </span>
              <a
                href={withNetworkUtm("https://shrinkmd.com/", "site_layout")}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit shrinkMD, founded by Shariq Refai, MD, MBA (opens in a new tab)."
                className="inline-flex items-center bg-white/95 hover:bg-white transition-colors px-3 py-1.5 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#171717]"
              >
                <img
                  src={shrinkMDLogo}
                  alt="shrinkMD wordmark"
                  className="h-5 w-auto object-contain" width={240} height={65} loading="lazy" decoding="async"/>
              </a>
              <a
                href={withNetworkUtm("https://shrinq.com/", "site_layout")}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit shrinQ, founded by Shariq Refai, MD, MBA (opens in a new tab)."
                className="inline-flex items-center bg-[#1B2B29] hover:bg-[#243a37] transition-colors px-3 py-1.5 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#171717]"
              >
                <img
                  src={shrinQLogo}
                  alt="shrinQ wordmark"
                  className="h-5 w-auto object-contain" width={1283} height={345} loading="lazy" decoding="async"/>
              </a>
            </div>

            <p className="mt-6 text-xs text-gray-400 leading-relaxed font-light">
              <span className="uppercase tracking-widest text-gray-400 font-medium mr-2">
                Medical editor for
              </span>
              <a
                href={withNetworkUtm("https://anxietyresource.org/", "site_layout")}
                rel="noopener"
                className="text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
              >
                AnxietyResource.org
              </a>
              <span className="text-gray-500 mx-2">&middot;</span>
              <a
                href={withNetworkUtm("https://depressionresource.org/", "site_layout")}
                rel="noopener"
                className="text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
              >
                DepressionResource.org
              </a>
              <span className="text-gray-500 mx-2">&middot;</span>
              <a
                href={withNetworkUtm("https://anxietyresearch.org/", "site_layout")}
                rel="noopener"
                className="text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
              >
                AnxietyResearch.org
              </a>
              <span className="text-gray-500 mx-2">&middot;</span>
              <a
                href={withNetworkUtm("https://psychiatryrx.org/", "site_layout")}
                rel="noopener"
                className="text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
              >
                PsychiatryRx.org
              </a>
              <span className="text-gray-500 mx-2">&middot;</span>
              <a
                href={withNetworkUtm("https://shrinkdaily.com/", "site_layout")}
                rel="noopener"
                className="text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
              >
                ShrinkDaily.com
              </a>
            </p>

            <div className="mt-8 pt-6 border-t border-white/10">
              <a
                href={withNetworkUtm("https://shrinknetwork.com/", "site_layout")}
                rel="noopener"
                aria-label="Part of The Shrink Network. Visit ShrinkNetwork.com, the network's front door."
                className="inline-block text-sm font-medium tracking-wide text-[#58B8B1] hover:text-[#7fd0c9] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-[#171717] rounded-sm"
              >
                Part of The Shrink Network
              </a>
              <p className="mt-3 text-xs text-gray-400 leading-relaxed font-light max-w-prose">
                <a
                  href={withNetworkUtm("https://shrinknetwork.com/", "site_layout")}
                  rel="noopener"
                  className="text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                >
                  The Shrink Network
                </a>{" "}
                is a group of independent mental health
                properties founded and medically reviewed by Shariq Refai, MD,
                MBA. Clinical care, education, wellness tools, and publishing are
                kept operationally separate. Nothing in the network runs ads or
                earns referral revenue.{" "}
                <a
                  href={withNetworkUtm("https://shrinknetwork.com/", "site_layout")}
                  rel="noopener"
                  className="text-gray-300 underline decoration-[#58B8B1] decoration-2 underline-offset-4 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                >
                  Visit ShrinkNetwork.com
                </a>
              </p>
            </div>
          </div>

          <nav aria-label="Footer site" className="flex flex-col gap-4">
            <span className="footer-label text-sm font-semibold uppercase tracking-wider text-gray-400">
              Site
            </span>
            <ul className="grid grid-cols-2 gap-x-12 gap-y-3 list-none p-0">
              {navItems.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className="text-sm text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Footer legal" className="flex flex-col gap-4">
            <span className="footer-label text-sm font-semibold uppercase tracking-wider text-gray-400">
              Legal
            </span>
            <ul className="flex flex-col gap-3 list-none p-0">
              {legalItems.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className="text-sm text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Footer site resources" className="flex flex-col gap-4">
            <span className="footer-label text-sm font-semibold uppercase tracking-wider text-gray-400">
              Site Resources
            </span>
            <ul className="flex flex-col gap-3 list-none p-0">
              {siteResourceItems.map((item) => (
                <li key={item.href}>
                  <Link
                    href={item.href}
                    className="text-sm text-gray-300 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

        </div>

        <NetworkFooter />

        <div className="container mx-auto px-4 md:px-8 py-6 border-t border-white/10 text-xs text-gray-400 flex flex-col sm:flex-row justify-between gap-3 sm:items-center">
          <span>
            &copy; 2026 Shariq Refai, MD, MBA. All rights
            reserved.
          </span>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            <a
              href={withNetworkUtm("https://shrinknetwork.com/", "site_layout")}
              rel="noopener"
              aria-label="Visit ShrinkNetwork.com, the front door of The Shrink Network."
              className="text-gray-300 hover:text-white transition-colors tracking-wide focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
            >
              ShrinkNetwork.com
            </a>
            <a
              href="mailto:hello@shariqrefai.com"
              aria-label="Email hello@shariqrefai.com"
              className="text-gray-300 hover:text-white transition-colors tracking-wide focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
            >
              hello@shariqrefai.com
            </a>
            <Link
              href="/media-inquiries"
              className="text-gray-300 hover:text-white transition-colors tracking-wide focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
            >
              Media Inquiries
            </Link>
            <a
              href="https://www.linkedin.com/in/shariqrefai"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit Shariq Refai, MD, MBA on LinkedIn (opens in a new tab)."
              className="text-gray-300 hover:text-white transition-colors tracking-wide focus:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded-sm"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
      <ConsentBanner />
    </div>
  );
}
